import { GitHub, Instagram, LinkedIn, X, YouTube } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { Mail, MapPin } from 'lucide-react';

import logo from '@src/assets/logo.png';
import { openConsentPreferences } from '@src/components/consent';
import { DisplayYear } from '@src/components/RealDate.jsx';
import { useNavigateToTop } from '@src/hooks/useNavigateToTop';

import { Button } from './../../components/ui/button';

export function Footer() {
  const onNavigateToTop = useNavigateToTop();
  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8">
          {/* SecureOneLabs / BOSSS Info */}
          <div className="lg:col-span-2">
            <div className="mb-6">
              <img
                src={logo}
                alt="SecureOneLabs - BOSSS"
                className="h-8 w-auto mb-4 filter brightness-0 invert"
              />

              <span className="text-xs text-blue-400 font-medium block">BOSSS — Unified Cybersecurity Platform</span>
            </div>

            <p className="text-gray-400 mb-6 leading-relaxed">
              Back Office Security Support System — the all-in-one cybersecurity platform
              for GRC, IAM, CMDB, XDR, Service Desk, Penetration Testing, Documents, and Business Management.
            </p>

            <div className="space-y-3">
              <div className="flex items-center text-gray-400">
                <Mail className="h-4 w-4 mr-3" />
                <span>info@secureonelabs.com</span>
              </div>

              <div className="flex items-start text-gray-400">
                <MapPin className="h-4 w-4 mr-3 mt-1" />

                <address className="not-italic">
                  <span className="block">701 Tillery Street #12</span>
                  <span className="block">Austin, Texas 78702</span>
                </address>
              </div>
            </div>
          </div>

          {/* Products */}
          <div>
            <h3 className="font-semibold mb-4">Products</h3>

            <ul className="space-y-2">
              <li>
                <button
                  className="text-gray-400 hover:text-white transition-colors text-left text-sm"
                  onClick={() => onNavigateToTop('/platform/ai-agents')}
                >
                  AI Voice Agents
                </button>
              </li>

              <li>
                <button
                  className="text-gray-400 hover:text-white transition-colors text-left text-sm"
                  onClick={() => onNavigateToTop('/platform/resilience')}
                >
                  BCRP
                </button>
              </li>

              <li>
                <button
                  className="text-gray-400 hover:text-white transition-colors text-left text-sm"
                  onClick={() => onNavigateToTop('/platform/business')}
                >
                  Business
                </button>
              </li>

              <li>
                <button
                  className="text-gray-400 hover:text-white transition-colors text-left text-sm"
                  onClick={() => onNavigateToTop('/platform/cmdb')}
                >
                  CMDB
                </button>
              </li>

              <li>
                <button
                  className="text-gray-400 hover:text-white transition-colors text-left text-sm"
                  onClick={() => onNavigateToTop('/platform/documents')}
                >
                  Documents
                </button>
              </li>

              <li>
                <button
                  className="text-gray-400 hover:text-white transition-colors text-left text-sm"
                  onClick={() => onNavigateToTop('/platform/grc')}
                >
                  GRC
                </button>
              </li>

              <li>
                <button
                  className="text-gray-400 hover:text-white transition-colors text-left text-sm"
                  onClick={() => onNavigateToTop('/platform/iam')}
                >
                  IAM
                </button>
              </li>

              <li>
                <button
                  className="text-gray-400 hover:text-white transition-colors text-left text-sm"
                  onClick={() => onNavigateToTop('/platform/pentest')}
                >
                  Penetration Testing
                </button>
              </li>

              <li>
                <button
                  className="text-gray-400 hover:text-white transition-colors text-left text-sm"
                  onClick={() => onNavigateToTop('/platform/service-desk')}
                >
                  Service Desk
                </button>
              </li>

              <li>
                <button
                  className="text-gray-400 hover:text-white transition-colors text-left text-sm"
                  onClick={() => onNavigateToTop('/platform/xdr')}
                >
                  XDR
                </button>
              </li>
            </ul>
          </div>

          {/* Industries */}
          <div>
            <h3 className="font-semibold mb-4">Industries</h3>

            <ul className="space-y-2">
              <li>
                <button
                  className="text-gray-400 hover:text-white transition-colors text-left text-sm"
                  onClick={() => onNavigateToTop('/industries/financial')}
                >
                  Financial Services
                </button>
              </li>

              <li>
                <button
                  className="text-gray-400 hover:text-white transition-colors text-left text-sm"
                  onClick={() => onNavigateToTop('/industries/healthcare')}
                >
                  Healthcare
                </button>
              </li>

              <li>
                <button
                  className="text-gray-400 hover:text-white transition-colors text-left text-sm"
                  onClick={() => onNavigateToTop('/industries/education')}
                >
                  Education
                </button>
              </li>

              <li>
                <button
                  className="text-gray-400 hover:text-white transition-colors text-left text-sm"
                  onClick={() => onNavigateToTop('/industries/government')}
                >
                  Government
                </button>
              </li>

              <li>
                <button
                  className="text-gray-400 hover:text-white transition-colors text-left text-sm"
                  onClick={() => onNavigateToTop('/industries/manufacturing')}
                >
                  Manufacturing
                </button>
              </li>

              <li>
                <button
                  className="text-gray-400 hover:text-white transition-colors text-left text-sm"
                  onClick={() => onNavigateToTop('/industries/retail')}
                >
                  Retail
                </button>
              </li>

              <li>
                <button
                  className="text-gray-400 hover:text-white transition-colors text-left text-sm"
                  onClick={() => onNavigateToTop('/industries/mssp')}
                >
                  MSSP
                </button>
              </li>
            </ul>
          </div>

          {/* Compare & Services */}
          <div>
            <h3 className="font-semibold mb-4">Compare</h3>

            <ul className="space-y-2 mb-6">
              <li>
                <button
                  className="text-gray-400 hover:text-white transition-colors text-left text-sm"
                  onClick={() => onNavigateToTop('/compare/servicenow')}
                >
                  vs ServiceNow
                </button>
              </li>

              <li>
                <button
                  className="text-gray-400 hover:text-white transition-colors text-left text-sm"
                  onClick={() => onNavigateToTop('/compare/splunk')}
                >
                  vs Splunk
                </button>
              </li>

              <li>
                <button
                  className="text-gray-400 hover:text-white transition-colors text-left text-sm"
                  onClick={() => onNavigateToTop('/compare/crowdstrike')}
                >
                  vs CrowdStrike
                </button>
              </li>

              <li>
                <button
                  className="text-gray-400 hover:text-white transition-colors text-left text-sm"
                  onClick={() => onNavigateToTop('/compare/sailpoint')}
                >
                  vs SailPoint
                </button>
              </li>

              <li>
                <button
                  className="text-gray-400 hover:text-white transition-colors text-left text-sm"
                  onClick={() => onNavigateToTop('/compare/archer')}
                >
                  vs Archer
                </button>
              </li>

              <li>
                <button
                  className="text-gray-400 hover:text-white transition-colors text-left text-sm"
                  onClick={() => onNavigateToTop('/compare/qualys')}
                >
                  vs Qualys
                </button>
              </li>
            </ul>
          </div>

          {/* CTA & Legal */}
          <div>
            <h3 className="font-semibold mb-4">Get Started</h3>

            <p className="text-gray-400 mb-4 text-sm">
              Ready to unify your cybersecurity operations?
            </p>

            <motion.div
              whileTap={{ scale: 0.95 }}
              whileHover={{ scale: 1.05 }}
            >
              <Button
                className="w-full bg-blue-600 hover:bg-blue-700 text-white mb-6 transition-all hover:shadow-lg"
                onClick={() => onNavigateToTop('/demo')}
              >
                Schedule Demo
              </Button>
            </motion.div>

            <div className="space-y-2">
              <button
                className="block text-gray-400 hover:text-white transition-colors text-sm"
                onClick={() => onNavigateToTop('/team')}
              >
                Team
              </button>

              <button
                className="block text-gray-400 hover:text-white transition-colors text-sm"
                onClick={() => onNavigateToTop('/contact')}
              >
                Contact Us
              </button>

              <button
                className="block text-gray-400 hover:text-white transition-colors text-sm"
                onClick={() => onNavigateToTop('/privacy-policy')}
              >
                Privacy Policy
              </button>

              <button
                className="block text-gray-400 hover:text-white transition-colors text-sm"
                onClick={() => onNavigateToTop('/terms-of-service')}
              >
                Terms
              </button>

              <button
                className="block text-gray-400 hover:text-white transition-colors text-sm"
                onClick={() => onNavigateToTop('/cookie-policy')}
              >
                Cookie Policy
              </button>

              {/* Withdrawing consent must be as easy as giving it, so this sits
                  on every page alongside the other legal links. */}
              <button
                className="block text-gray-400 hover:text-white transition-colors text-sm"
                onClick={openConsentPreferences}
              >
                Cookie Preferences
              </button>

              <button
                className="block text-gray-400 hover:text-white transition-colors text-sm"
                onClick={() => onNavigateToTop('/security')}
              >
                Security
              </button>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col lg:flex-row justify-between items-center">
          <p className="text-gray-400 text-sm">
            ©
            {' '}
            <DisplayYear />
            {' '}
            SecureOneLabs — BOSSS Back Office Security Support System. All rights reserved.
          </p>

          <div className="flex items-center space-x-4 mt-4 lg:mt-0">
            <span className="text-gray-400 text-sm">Connect with us:</span>

            <motion.a
              target="_blank"
              title="LinkedIn"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.15 }}
              href="https://www.linkedin.com/company/secureonelabs"
              className="text-gray-400 hover:text-white transition-colors"
            >
              <LinkedIn className="w-5 h-5" />
            </motion.a>

            <motion.a
              title="X"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.15 }}
              href="https://x.com/secureonelabs"
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </motion.a>

            <motion.a
              target="_blank"
              title="YouTube"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.15 }}
              href="https://www.youtube.com/@secureonelabs"
              className="text-gray-400 hover:text-white transition-colors"
            >
              <YouTube className="w-5 h-5" />
            </motion.a>

            <motion.a
              title="GitHub"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.15 }}
              href="https://github.com/secureonelabs"
              className="text-gray-400 hover:text-white transition-colors"
            >
              <GitHub className="w-5 h-5" />
            </motion.a>

            <motion.a
              target="_blank"
              title="Instagram"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.15 }}
              href="https://www.instagram.com/secureonelabs/"
              className="text-gray-400 hover:text-white transition-colors"
            >
              <Instagram className="w-5 h-5" />
            </motion.a>

            <motion.a
              title="TikTok"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.15 }}
              href="https://www.tiktok.com/@secureonelabs"
              className="text-gray-400 hover:text-white transition-colors"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path
                  d={
                    'M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 '
                    + '6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z'
                  }
                />
              </svg>
            </motion.a>
          </div>
        </div>
      </div>
    </footer>
  );
}

Footer.displayName = 'Footer';
export default Footer;
